import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Calculator, Check } from "lucide-react";
import { trackEvent } from "../../lib/api";
import { ISDE_PATH } from "../../lib/isde";
import { ISDE_HOME } from "../../data/isdeContent";
import { SectionHeading } from "./CtaButtons";

const GLASS = [["hr_plus_plus", "HR++"], ["hr_triple", "HR+++"]];

// Compacte, interactieve preview; toont bewust geen bedrag (dat volgt pas uit de echte subsidiecheck).
const Preview = () => {
  const navigate = useNavigate();
  const [glass, setGlass] = useState("hr_triple");
  const [m2, setM2] = useState(15);
  const start = () => {
    trackEvent("cta_isde_click", { location: "home-isde-preview" });
    navigate(`${ISDE_PATH}?glas=${glass}&m2=${m2}#subsidiecheck`);
  };
  return (
    <div className="relative overflow-hidden rounded-3xl bg-ink p-6 sm:p-8" data-testid="home-isde-preview">
      <div className="hero-grid-dark absolute inset-0 opacity-60" aria-hidden="true" />
      <div className="relative">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-[#D9B97A]"><Calculator className="h-5 w-5" aria-hidden="true" /></span>
          <p className="font-display text-[18px] font-semibold text-white">Ontdek uw mogelijke subsidie</p>
        </div>
        <p className="mt-6 text-[13px] font-semibold uppercase tracking-[0.12em] text-slate-400" id="isde-preview-glass">Glaskeuze</p>
        <div className="mt-2 grid grid-cols-2 gap-2" role="radiogroup" aria-labelledby="isde-preview-glass">
          {GLASS.map(([id, l]) => (
            <button key={id} type="button" role="radio" aria-checked={glass === id} onClick={() => setGlass(id)} data-testid={`home-isde-glass-${id}`}
              className={`min-h-[48px] rounded-xl border text-[15px] font-semibold transition-colors ${glass === id ? "border-[#D9B97A] bg-white/10 text-white" : "border-white/15 text-slate-300 hover:border-white/30"}`}>{l}</button>
          ))}
        </div>
        <label htmlFor="isde-preview-m2" className="mt-6 flex items-center justify-between text-[13px] font-semibold uppercase tracking-[0.12em] text-slate-400">
          Geschat oppervlak <span className="text-[16px] normal-case tracking-normal text-white" data-testid="home-isde-m2">{m2} m²</span>
        </label>
        <input id="isde-preview-m2" type="range" min={3} max={45} value={m2} onChange={(e) => setM2(Number(e.target.value))} data-testid="home-isde-m2-slider"
          className="mt-3 w-full accent-[#D9B97A]" />
        <button type="button" onClick={start} className="btn-cta group mt-7 w-full" data-testid="home-isde-start">
          Start subsidiecheck <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </button>
        <p className="mt-4 text-[12.5px] leading-relaxed text-slate-400">Voorbeeldinvoer. Het bedrag berekenen we in de subsidiecheck op basis van uw woning en de RVO-regels; geen contactgegevens nodig.</p>
      </div>
    </div>
  );
};

export const IsdeBlock = ({ block }) => (
  <section id="isde-subsidie" aria-labelledby="isde-block-title" className="section scroll-mt-20 border-y border-slate-200 bg-slate-50" data-testid="home-isde-block">
    <div className="container-tight grid items-center gap-10 lg:grid-cols-12 lg:gap-16">
      <div className="lg:col-span-6">
        <SectionHeading id="isde-block-title" eyebrow={ISDE_HOME.eyebrow} title={block.title} lead={block.body} />
        <ul className="mt-7 space-y-4">
          {ISDE_HOME.benefits.map(([t, d], i) => (
            <li key={t} className="reveal flex gap-3" style={{ animationDelay: `${80 + i * 80}ms` }} data-testid={`home-isde-benefit-${i}`}>
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold-soft text-gold"><Check className="h-3.5 w-3.5" aria-hidden="true" /></span>
              <span><span className="block font-semibold text-ink">{t}</span><span className="text-[15px] text-slate-600">{d}</span></span>
            </li>
          ))}
        </ul>
        <div className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
          <Link to={`${ISDE_PATH}#subsidiecheck`} onClick={() => trackEvent("cta_isde_click", { location: "home-isde-block" })} data-testid="home-isde-cta"
            className="btn-primary group min-h-[52px] px-6">Bereken uw mogelijke subsidie <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" /></Link>
          <Link to={`${ISDE_PATH}#voorwaarden`} className="btn-ghost" data-testid="home-isde-conditions">Bekijk de ISDE-voorwaarden</Link>
        </div>
        <p className="mt-4 text-[13px] text-slate-500" data-testid="home-isde-disclaimer">Een uitkomst is altijd een indicatie, geen toezegging. Maas Kozijn verstrekt geen subsidie; RVO beoordeelt elke aanvraag.</p>
      </div>
      <div className="lg:col-span-6"><Preview /></div>
    </div>
  </section>
);
